// The cast the automated scenarios share.
//
// Two layers, which is how a normal methodology files this: IT general controls are their own
// population, assessed per application; everything else lives in the business process it serves,
// numbered with that process's prefix. An automated control is not a separate population — it sits
// in the process RACM beside the manual ones, flagged as automated, and leans on the ITGCs of the
// application that runs it. IT-dependent manual controls lean on them too, for the integrity of the
// reports their reviewer reads.
import {Conclusion, DemoControl} from "../model";

export const SAP = "SAP S/4HANA";
export const WORKDAY = "Workday";
export const CONCUR = "SAP Concur";
export const LEGACY = "Legacy ledger";

/** Every application the ITGCs are assessed against. */
export const APPS = [SAP, WORKDAY, CONCUR];

export const effective = (justification: string): Conclusion => ({
  outcome: "operated_effectively",
  supportsReliance: true,
  justification,
});

export const withExceptions = (justification: string): Conclusion => ({
  outcome: "operated_with_exceptions",
  supportsReliance: true,
  justification,
});

export const failed = (justification: string): Conclusion => ({
  outcome: "did_not_operate_effectively",
  supportsReliance: false,
  justification,
});

/** An ITGC assessed per application; a scope left out of `oe` has not concluded yet. */
const itgc = (ref: string, title: string, oe: Partial<Record<string, Conclusion>>): DemoControl => ({
  ref,
  title,
  assessment: "GITC Assessment",
  scopes: APPS.map(scope => ({scope, design: "effective", implementation: "effective", oe: oe[scope]})),
});

/** A control in a business process. `automated` marks the ones the application performs itself. */
const processControl = (
  ref: string,
  title: string,
  assessment: string,
  automated: boolean,
  oe?: Conclusion,
): DemoControl => ({
  ref,
  title,
  assessment,
  automated,
  scopes: [{scope: "Global", design: "effective", implementation: "effective", oe}],
});

// ─── IT general controls ──────────────────────────────────────────────────────

export const privilegedAccess = itgc("ITGC-01", "Privileged access provisioning", {
  [SAP]: effective("Every privileged grant in the period was traced to an approved request."),
  [WORKDAY]: withExceptions(
    "One grant was approved retrospectively, two days after provisioning. Isolated, and the access " +
      "granted was within the requester's existing role.",
  ),
  [CONCUR]: effective("No privileged grants were made in the period."),
});

export const changeManagement = itgc("ITGC-02", "Change management", {
  [SAP]: effective("A sample of 25 changes was tested; all were approved and tested before release."),
  [WORKDAY]: effective("A sample of 15 changes was tested with no exceptions."),
  [CONCUR]: effective("All 6 configuration changes in the period were approved before release."),
});

export const logicalAccess = itgc("ITGC-03", "Logical access", {
  [SAP]: effective("Joiner, mover and leaver access was tested across the period with no exceptions."),
  [WORKDAY]: effective("Quarterly access reviews were completed and evidenced for all four quarters."),
  [CONCUR]: effective("Joiner and leaver access was tested with no exceptions."),
});

/** Not yet concluded anywhere — the "not concluded yet" state has to be visible somewhere. */
export const batchJobs = itgc("ITGC-04", "Batch job scheduling and monitoring", {});

/** Logical access failing at SAP only, so the other applications stay green beside it. */
export const logicalAccessFailedAtSap = itgc("ITGC-03", "Logical access", {
  [SAP]: failed(
    "Three leavers retained access to the finance application beyond their termination date, " +
      "identified during the Q4 access review. The control is concluded not to have operated " +
      "effectively across the period and no reliance is placed on it.",
  ),
  [WORKDAY]: effective("Quarterly access reviews were completed and evidenced for all four quarters."),
  [CONCUR]: effective("Joiner and leaver access was tested with no exceptions."),
});

// ─── Vendor Invoice ───────────────────────────────────────────────────────────

export const threeWayMatch = (oe?: Conclusion, scope = "Global"): DemoControl => ({
  ref: "VI-02",
  title: "Three-way match",
  assessment: "Vendor Invoice",
  automated: true,
  scopes: [{scope, design: "effective", implementation: "effective", oe}],
});

export const duplicateInvoice = processControl(
  "VI-04",
  "Duplicate invoice check",
  "Vendor Invoice",
  true,
  effective(
    "The application blocks a second invoice carrying a supplier and reference already posted. " +
      "Confirmed by re-performance, and relied upon on the basis of the SAP ITGCs.",
  ),
);

export const goodsReceiptReview = processControl(
  "VI-06",
  "Goods receipt matching review",
  "Vendor Invoice",
  false,
  withExceptions(
    "Two of 25 receipts were matched a day after the invoice posted. Both were subsequently agreed to " +
      "the delivery note; the sequence exception did not affect the amount recorded.",
  ),
);

// ─── Order to Cash ────────────────────────────────────────────────────────────

export const creditLimit = (oe?: Conclusion): DemoControl =>
  processControl("O2C-01", "Automated credit-limit check", "Order to Cash", true, oe);

export const customerMasterReview = processControl(
  "O2C-06",
  "Customer master data change review",
  "Order to Cash",
  false,
  effective("All 42 customer master changes in the period were reviewed against an approved request."),
);

/** Left unconcluded so a control still in flight is visible on the map. */
export const revenueCutOff = processControl("O2C-09", "Revenue cut-off review", "Order to Cash", false);

// ─── Payroll ──────────────────────────────────────────────────────────────────

export const payrollChangeApproval = processControl(
  "PAY-04",
  "Payroll change approval",
  "Payroll",
  true,
  effective("The application requires a second approver before a pay change takes effect."),
);

export const payrollReconciliation = processControl(
  "PAY-06",
  "Payroll reconciliation review",
  "Payroll",
  false,
  effective("Month-on-month payroll movements were reconciled and reviewed for all twelve periods."),
);

const automatedOn = (app: string) => `Declared on the control — the application performs it in ${app}`;
const reportFrom = (app: string) => `Declared on the control — the report reviewed is produced by ${app}`;

/**
 * Two layers: the ITGCs of an application underneath the controls that application runs, and
 * underneath the manual reviews whose reports it produces. Nothing depends on a process control — a
 * manual review and an automated check are peers over the same risk, not one atop the other.
 */
export const CAST_EDGES = [
  // Vendor Invoice runs in SAP.
  {from: "ITGC-02", to: "VI-02", scope: SAP, provenance: automatedOn(SAP)},
  {from: "ITGC-03", to: "VI-02", scope: SAP, provenance: automatedOn(SAP)},
  {
    from: "ITGC-01",
    to: "VI-04",
    scope: SAP,
    provenance: "Declared on the control — the check's configuration is privileged",
  },
  {from: "ITGC-02", to: "VI-04", scope: SAP, provenance: automatedOn(SAP)},
  {from: "ITGC-03", to: "VI-04", scope: SAP, provenance: automatedOn(SAP)},
  {
    from: "ITGC-04",
    to: "VI-04",
    scope: SAP,
    provenance: "Declared on the control — the check runs in the nightly posting job",
  },
  {from: "ITGC-03", to: "VI-06", scope: SAP, provenance: reportFrom(SAP)},

  // Order to Cash runs in SAP too.
  {from: "ITGC-02", to: "O2C-01", scope: SAP, provenance: automatedOn(SAP)},
  {from: "ITGC-03", to: "O2C-01", scope: SAP, provenance: automatedOn(SAP)},
  {from: "ITGC-03", to: "O2C-06", scope: SAP, provenance: reportFrom(SAP)},
  {
    from: "ITGC-04",
    to: "O2C-09",
    scope: SAP,
    provenance: "Declared on the control — cut-off rests on the nightly posting job",
  },

  // Payroll runs in Workday.
  {from: "ITGC-02", to: "PAY-04", scope: WORKDAY, provenance: automatedOn(WORKDAY)},
  {from: "ITGC-03", to: "PAY-04", scope: WORKDAY, provenance: automatedOn(WORKDAY)},
  {from: "ITGC-03", to: "PAY-06", scope: WORKDAY, provenance: reportFrom(WORKDAY)},
];
